import { useCallback, useRef } from "react";

import { scrollWorkspaceTarget, workspaceTargetMargin } from "./scrollWorkspaceTarget";

/** Keep a deep link aligned while async sections settle, until the user takes control. */
export function useProjectAnchor(hash: string, projectId: string | undefined) {
  const stop = useRef<(() => void) | undefined>();
  return useCallback(
    (root: HTMLDivElement | null) => {
      stop.current?.();
      stop.current = undefined;
      if (
        !root ||
        !projectId ||
        !["#census", "#quote-requests", "#project-lifecycle"].includes(hash)
      )
        return;
      const target = root.querySelector<HTMLElement>(hash);
      if (!target) return;
      const doc = root.ownerDocument;
      const win = doc.defaultView;
      if (!win) return;
      const bars = Array.from(doc.querySelectorAll<HTMLElement>("[data-project-anchor-occluder]"));
      const originalMargin = target.style.scrollMarginTop;
      const measureMargin = () => {
        const margin = workspaceTargetMargin(target);
        target.style.scrollMarginTop = `${margin}px`;
        return margin;
      };
      let active = true;
      let frame: number | undefined;
      const align = () => {
        if (!active || frame !== undefined) return;
        frame = requestAnimationFrame(() => {
          frame = undefined;
          if (active) {
            scrollWorkspaceTarget(target, measureMargin());
          }
        });
      };
      const observer =
        typeof ResizeObserver === "undefined" ? undefined : new ResizeObserver(align);
      const events = ["wheel", "touchstart", "pointerdown", "keydown"] as const;
      const cleanup = () => {
        active = false;
        observer?.disconnect();
        if (frame !== undefined) cancelAnimationFrame(frame);
        frame = undefined;
        for (const event of events) doc.removeEventListener(event, cleanup, true);
      };
      stop.current = () => {
        cleanup();
        target.style.scrollMarginTop = originalMargin;
      };
      for (const event of events)
        doc.addEventListener(event, cleanup, { capture: true, passive: true });
      observer?.observe(root);
      observer?.observe(target);
      for (const bar of bars) observer?.observe(bar);
      target.focus({ preventScroll: true });
      align();
    },
    [hash, projectId],
  );
}
